import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge } from "../core/badge";
import { Button } from "../core/button";

// Generic interface for sortable items
export interface SortableDataItem {
  id?: string;
  name?: string;
  description?: string;
  order?: number;
  [key: string]: unknown;
}

export interface SortableContentItemProps<T extends SortableDataItem> {
  item: T;
  index: number;
  onRemove: (item: T) => void;
  idKey?: keyof T; // Which property to use as id, defaults to "id"
  nameKey?: keyof T; // Which property to use as name, defaults to "name"
  descriptionKey?: keyof T; // Which property to use as description, defaults to "description"
  // Optional render props for customization
  renderContent?: (item: T, index: number) => React.ReactNode;
  renderActions?: (item: T, onRemove: (item: T) => void) => React.ReactNode;
}

const resolveItemValue = <T extends SortableDataItem>(
  item: T,
  key?: keyof T,
  fallback?: keyof SortableDataItem
) => {
  if (key && item[key] != null) {
    return item[key];
  }

  if (fallback && (item as SortableDataItem)[fallback] != null) {
    return (item as SortableDataItem)[fallback];
  }

  return undefined;
};

export function SortableContentItem<T extends SortableDataItem>({
  item,
  index,
  onRemove,
  renderContent,
  renderActions,
  idKey,
  nameKey,
  descriptionKey,
}: SortableContentItemProps<T>) {
  const rawId = resolveItemValue(item, idKey, 'id');
  const fallbackId = item.id ?? index;
  const itemId = rawId != null ? String(rawId) : String(fallbackId);

  const rawName = resolveItemValue(item, nameKey, 'name');
  const itemName = rawName != null ? String(rawName) : `Item ${index + 1}`;

  const rawDescription = resolveItemValue(item, descriptionKey, 'description');
  const itemDescription = rawDescription != null ? String(rawDescription) : undefined;

  // Set up sortable functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Default content render function
  const defaultRenderContent = () => (
    <div className="flex items-center">
      <Badge variant="outline" className="w-6 h-6 flex items-center justify-center rounded-full mr-3 font-medium text-sm p-0">
        {index + 1}
      </Badge>
      <div>
        <h4 className="font-medium text-gray-900">{itemName}</h4>
        {itemDescription && (
          <p className="text-sm text-gray-500 mt-0.5">{itemDescription}</p>
        )}
      </div>
    </div>
  );

  // Default actions render function
  const defaultRenderActions = (item: T, onRemove: (item: T) => void) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={(e) => {
        // Stop propagation to prevent drag handlers from capturing this event
        e.stopPropagation();
        // Prevent default browser behavior
        e.preventDefault();
        // Call the remove function
        onRemove(item);
      }}
      className="text-gray-500 hover:text-red-600"
      // Make sure the button doesn't get draggable attributes
      data-no-dnd="true"
    >
      Remove
    </Button>
  );

  return (
    <li
      ref={setNodeRef}
      style={{...style, touchAction: 'none'}}
      className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-move border-b border-gray-100 last:border-b-0"
      {...attributes}
      {...listeners}
    >
      <div className="flex-grow min-w-0">
        {renderContent ? renderContent(item, index) : defaultRenderContent()}
      </div>
      <div
        className="flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{ touchAction: 'auto' }}
      >
        {renderActions ? renderActions(item, onRemove) : defaultRenderActions(item, onRemove)}
      </div>
    </li>
  );
}

// Container component to wrap the sortable content list
export interface SortableContentListProps<T extends SortableDataItem> {
  items: T[];
  onRemove: (item: T) => void;
  renderItem?: (item: T, index: number) => React.ReactNode;
  renderItemActions?: (item: T, onRemove: (item: T) => void) => React.ReactNode;
  idKey?: keyof T;
  nameKey?: keyof T;
  descriptionKey?: keyof T;
}

export function SortableContentList<T extends SortableDataItem>({
  items,
  onRemove,
  renderItem,
  renderItemActions,
  idKey,
  nameKey,
  descriptionKey,
}: SortableContentListProps<T>) {
  return (
    <ul className="divide-y divide-gray-100 p-1">
      {items.map((item, index) => (
        <SortableContentItem
          key={item.id}
          item={item}
          index={index}
          onRemove={onRemove}
          renderContent={renderItem}
          renderActions={renderItemActions}
          idKey={idKey}
          nameKey={nameKey}
          descriptionKey={descriptionKey}
        />
      ))}
    </ul>
  );
}